/**
 * Extract visible Q&A pairs from Kirby layout blocks (heading ending in ?
 * followed by one or more text blocks, or HTML h2/h3 + following content
 * inside text blocks). Used for FAQPage JSON-LD when the page has no
 * dedicated faqs structure field.
 */

export interface LayoutFaqItem {
  question: string;
  answer: string;
}

function stripTags(html: string): string {
  return String(html ?? "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isQuestionHeading(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  if (t.endsWith("?")) return true;
  // FAQ-style headings without "?": avoid section titles like "What We Provide"
  return /^(why|how|do i|can i|is |are |should )\b/i.test(t);
}

type BlockContent = {
  level?: string;
  text?: string;
  link?: string;
  accordion?: unknown[];
  image?: unknown;
  src?: string;
  tipHeading?: string;
  tipText?: string;
  question?: string;
  answer?: string;
};

/**
 * Kirby sometimes returns blocks with type: null; infer from content shape.
 */
function resolveBlockType(
  type: string | null | undefined,
  content: BlockContent,
): string {
  if (type) return type;
  if (Array.isArray(content.accordion)) return "accordion";
  if (content.level && content.text != null) return "heading";
  if (content.image != null || content.src) return "image";
  if (content.text != null && (content.link != null || !content.level)) {
    return "text";
  }
  return "";
}

/**
 * Pull Q&A pairs from HTML that embeds h2/h3 headings inside a text block
 * (common on essential-info pages).
 */
function extractFaqsFromHtml(html: string): LayoutFaqItem[] {
  if (!html || !/[<]h[23]\b/i.test(html)) return [];

  const faqs: LayoutFaqItem[] = [];
  const parts = String(html).split(/(?=<h[23]\b)/i);

  for (const part of parts) {
    const match = part.match(
      /^<h[23]\b[^>]*>([\s\S]*?)<\/h[23]>([\s\S]*)$/i,
    );
    if (!match) continue;
    const question = stripTags(match[1]);
    if (!isQuestionHeading(question)) continue;
    const answer = String(match[2] ?? "").trim();
    if (!answer || !stripTags(answer)) continue;
    faqs.push({ question, answer });
  }

  return faqs;
}

/**
 * Walk Kirby layouts -> columns -> blocks and pair question headings with
 * the following text block(s). Also parses HTML headings inside text blocks.
 */
export function extractFaqsFromLayouts(layouts: unknown): LayoutFaqItem[] {
  if (!Array.isArray(layouts)) return [];

  const faqs: LayoutFaqItem[] = [];

  for (const layout of layouts) {
    const columns = (layout as { columns?: unknown[] })?.columns;
    if (!Array.isArray(columns)) continue;

    for (const column of columns) {
      const blocks = (column as { blocks?: unknown[] })?.blocks;
      if (!Array.isArray(blocks)) continue;

      let pendingQuestion: string | null = null;
      const answerParts: string[] = [];

      const flush = () => {
        if (!pendingQuestion || !answerParts.length) {
          pendingQuestion = null;
          answerParts.length = 0;
          return;
        }
        const answer = answerParts.join("\n\n").trim();
        if (answer) {
          faqs.push({ question: pendingQuestion, answer });
        }
        pendingQuestion = null;
        answerParts.length = 0;
      };

      for (const raw of blocks) {
        const block = raw as {
          type?: string | null;
          content?: BlockContent;
        };
        const content = block?.content ?? {};
        const type = resolveBlockType(block?.type, content);

        if (type === "accordion" && Array.isArray(content.accordion)) {
          flush();
          for (const panel of content.accordion as Array<
            Record<string, unknown>
          >) {
            const question = String(
              panel.question ?? panel.title ?? panel.tipHeading ?? "",
            ).trim();
            const answer = String(
              panel.answer ?? panel.text1 ?? panel.text ?? "",
            ).trim();
            if (question && answer) {
              faqs.push({ question, answer });
            }
          }
          continue;
        }

        if (type === "heading") {
          const headingText = stripTags(String(content.text ?? ""));
          if (isQuestionHeading(headingText)) {
            flush();
            pendingQuestion = headingText;
            continue;
          }
          flush();
          continue;
        }

        if (type === "text") {
          const body = String(content.text ?? "").trim();
          if (!body) continue;

          // Prefer structured HTML Q&A inside the text block
          const nested = extractFaqsFromHtml(body);
          if (nested.length) {
            flush();
            faqs.push(...nested);
            continue;
          }

          if (pendingQuestion) {
            answerParts.push(body);
          }
          continue;
        }

        if (pendingQuestion && type && type !== "text") {
          flush();
        }
      }

      flush();
    }
  }

  return faqs;
}
